import { memo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableHead } from "@/components/spy-dashboard/SortableHead";
import {
  formatGscCount,
  formatGscCtr,
  formatGscPosition,
  getGscPositionToneClass,
} from "@/lib/gsc-display";
import { cn } from "@/lib/utils";
import { CopyIconButton } from "@/components/clicks/CopyableValue";
import {
  GSC_FILTER_KEYS,
  isGscDetailSortColumn,
  type GscDetailRow,
  type GscDetailSortColumn,
  type GscFilterKey,
  type GscPageSize,
  type GscSortDirection,
} from "@/types/gsc";
import { DetailTablePagination } from "@/components/gsc/DetailTablePagination";

interface DetailTableProps {
  rows: readonly GscDetailRow[];
  totalCount: number;
  page: number;
  pageSize: GscPageSize;
  sortBy: GscDetailSortColumn;
  sortDir: GscSortDirection;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: GscPageSize) => void;
  onSortChange: (column: GscDetailSortColumn) => void;
  onDrillDown?: (key: GscFilterKey, value: string) => void;
  isLoading?: boolean;
  isFetching?: boolean;
  className?: string;
}

const COLUMN_COUNT = 9;

const DRILL_DOWN_CELL_CLASS =
  "cursor-pointer transition-colors hover:underline hover:text-primary";

const getDeviceToneClass = (device: string): string => {
  const normalized = device.trim().toLowerCase();
  if (normalized === "desktop") return "text-blue-600 dark:text-blue-400";
  if (normalized === "mobile") return "text-emerald-600 dark:text-emerald-400";
  if (normalized === "tablet") return "text-amber-600 dark:text-amber-400";
  return "text-muted-foreground";
};

const DetailTableSkeleton = () => (
  <Card className="border-border shadow-none">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
      <Skeleton className="h-5 w-36" />
      <Skeleton className="h-4 w-24" />
    </CardHeader>
    <CardContent className="space-y-2 pt-0">
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </CardContent>
  </Card>
);

const detailRowKey = (row: GscDetailRow, index: number): string =>
  [
    row.domain,
    row.clean_url,
    row.keyword,
    row.country,
    row.device,
    row.record_date,
    index,
  ].join("|");

function DetailTableComponent({
  rows,
  totalCount,
  page,
  pageSize,
  sortBy,
  sortDir,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onDrillDown,
  isLoading = false,
  isFetching = false,
  className,
}: DetailTableProps) {
  const handleSort = (column: string): void => {
    if (!isGscDetailSortColumn(column)) return;
    onSortChange(column);
  };

  const handleDrillDown = (key: GscFilterKey, value: string): void => {
    const trimmed = value.trim();
    if (!trimmed || !onDrillDown) return;
    onDrillDown(key, trimmed);
  };

  if (isLoading) {
    return (
      <div className={className} aria-busy aria-label="Loading detail table">
        <DetailTableSkeleton />
      </div>
    );
  }

  const rowsLabel =
    totalCount === 1 ? "1 row" : `${totalCount.toLocaleString()} rows`;

  return (
    <Card
      className={cn(
        "border-border shadow-none",
        isFetching && "pointer-events-none opacity-60",
        className,
      )}
      aria-busy={isFetching}
      aria-label="Detail table"
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base font-semibold tracking-tight">
          Detail Table
        </CardTitle>
        <span className="text-xs text-muted-foreground tabular-nums">
          {rowsLabel}
        </span>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="relative max-h-[min(36rem,70vh)] overflow-auto rounded-lg border border-border">
          <Table>
            <TableHeader className="sticky top-0 z-10 border-b border-border bg-muted/30 backdrop-blur-sm">
              <TableRow className="border-b border-border hover:bg-transparent">
                <SortableHead
                  label="Domain"
                  column="domain"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
                <SortableHead
                  label="URL / Path"
                  column="clean_url"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Query"
                  column="keyword"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Country"
                  column="country"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Device"
                  column="device"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Clicks"
                  column="clicks"
                  align="right"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Impressions"
                  column="impressions"
                  align="right"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
                <SortableHead
                  label="CTR"
                  column="ctr"
                  align="right"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
                <SortableHead
                  label="Position"
                  column="avg_position"
                  align="right"
                  currentSort={sortBy}
                  isSortDesc={sortDir === "desc"}
                  onSort={handleSort}
                />
              </TableRow>
            </TableHeader>

            <TableBody>
              {rows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className="py-12 text-center text-sm text-muted-foreground"
                  >
                    No rows for this period and filters.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <TableRow
                    key={detailRowKey(row, index)}
                    className="hover:bg-muted/50"
                  >
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-1">
                        <button
                          type="button"
                          className={cn("text-left text-primary", DRILL_DOWN_CELL_CLASS)}
                          title={row.domain}
                          onClick={() =>
                            handleDrillDown(GSC_FILTER_KEYS.domain, row.domain)
                          }
                        >
                          {row.domain}
                        </button>
                        <CopyIconButton value={row.domain} />
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.clean_url ? (
                        <span className="flex items-center gap-1">
                          <button
                            type="button"
                            className={cn("text-left", DRILL_DOWN_CELL_CLASS)}
                            title={row.clean_url}
                            onClick={() =>
                              handleDrillDown(GSC_FILTER_KEYS.slug, row.clean_url)
                            }
                          >
                            {row.clean_url}
                          </button>
                          <CopyIconButton value={row.clean_url} />
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="max-w-[12rem]">
                      {row.keyword ? (
                        <button
                          type="button"
                          className={cn(
                            "block max-w-full truncate text-left font-mono text-[13px] text-slate-600 dark:text-slate-400",
                            DRILL_DOWN_CELL_CLASS,
                          )}
                          title={row.keyword}
                          onClick={() =>
                            handleDrillDown(GSC_FILTER_KEYS.query, row.keyword)
                          }
                        >
                          {row.keyword}
                        </button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {row.country ? (
                        <button
                          type="button"
                          className={cn(
                            "inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground",
                            DRILL_DOWN_CELL_CLASS,
                          )}
                          onClick={() =>
                            handleDrillDown(
                              GSC_FILTER_KEYS.country,
                              row.country,
                            )
                          }
                        >
                          {row.country}
                        </button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {row.device ? (
                        <button
                          type="button"
                          className={cn(
                            "text-xs font-medium tracking-wide uppercase",
                            getDeviceToneClass(row.device),
                            DRILL_DOWN_CELL_CLASS,
                          )}
                          onClick={() =>
                            handleDrillDown(GSC_FILTER_KEYS.device, row.device)
                          }
                        >
                          {row.device}
                        </button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums tracking-tight">
                      {formatGscCount(row.clicks)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums tracking-tight text-muted-foreground">
                      {formatGscCount(row.impressions)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums tracking-tight">
                      {formatGscCtr(row.ctr)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right tabular-nums tracking-tight",
                        getGscPositionToneClass(row.avg_position),
                      )}
                    >
                      {formatGscPosition(row.avg_position)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <DetailTablePagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </CardContent>
    </Card>
  );
}

export const DetailTable = memo(DetailTableComponent);
DetailTable.displayName = "DetailTable";
